import React, { useState, useEffect } from 'react';
import { db, ref, push } from '../firebase';
import { onValue, update, remove} from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

const AddProduct = () => {
  const navigate = useNavigate();
  const storage = getStorage();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: 'watches',
    type: '',
    brand: '',
    images: [],
    stock: '',
    featured: false,
    discount: 0,
    specifications: {
      material: '',
      colors: [],
      material: [],
      waterResistant: 'no',
      warranty: '',
      gender: 'unisex'
    }
  });
  const [editingId, setEditingId] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [view, setView] = useState('form');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentColor, setCurrentColor] = useState('');
  const [currentMaterial, setCurrentMaterial] = useState('');

// 3. Add handlers for color management
const handleAddColor = () => {
  if (currentColor.trim() && !product.specifications.colors.includes(currentColor.trim())) {
    setProduct(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        colors: [...prev.specifications.colors, currentColor.trim()]
      }
    }));
    setCurrentColor('');
  }
};

const handleRemoveColor = (colorToRemove) => {
  setProduct(prev => ({
    ...prev,
    specifications: {
      ...prev.specifications,
      colors: prev.specifications.colors.filter(color => color !== colorToRemove)
    }
  }));
};

// Material management handlers
const handleAddMaterial = () => {
  if (currentMaterial.trim() && !product.specifications.material.includes(currentMaterial.trim())) {
    setProduct(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        material: [...prev.specifications.material, currentMaterial.trim()]
      }
    }));
    setCurrentMaterial('');
  }
};

const handleRemoveMaterial = (materialToRemove) => {
  setProduct(prev => ({
    ...prev,
    specifications: {
      ...prev.specifications,
      material: prev.specifications.material.filter(material => material !== materialToRemove)
    }
  }));
};

  // Fetch products from Firebase
  useEffect(() => {
    const productsRef = ref(db, 'Watch/admin/products');
    const unsubscribe = onValue(productsRef, (snapshot) => {
      const productsData = snapshot.val();
      if (productsData) {
        const productsList = Object.entries(productsData).map(([id, data]) => ({
          id,
          ...data
        }));
        setProducts(productsList);
      } else {
        setProducts([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('specifications.')) {
      const specName = name.split('.')[1];
      let fieldValue;
      
      if (specName === 'waterResistant') {
        // Convert checkbox to "yes"/"no" for waterResistant field
        fieldValue = checked ? 'yes' : 'no';
      } else {
        fieldValue = type === 'checkbox' ? checked : value;
      }
      
      setProduct(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [specName]: fieldValue
        }
      }));
    } else {
      setProduct(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

const handleImageUpload = async (e) => {
  setUploading(true);
  const files = Array.from(e.target.files);
  
  try {
    const uploadPromises = files.map(async (file) => {
      // Create a storage reference
      const fileRef = storageRef(storage, `products/${Date.now()}_${file.name}`);
      
      // Upload the file
      await uploadBytes(fileRef, file);
      
      // Get the download URL
      return await getDownloadURL(fileRef);
    });
    
    // Wait for all uploads to complete
    const urls = await Promise.all(uploadPromises);
    
    setImageUrls(urls);
    setProduct(prev => ({ ...prev, images: urls }));
  } catch (error) {
    console.error("Error uploading images: ", error);
    setSuccess('Error uploading images. Please try again.');
  } finally {
    setUploading(false);
  }
};

useEffect(() => {
  return () => {
    // Clean up any object URLs we've created
    imageUrls.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  };
}, [imageUrls]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      if (editingId) {
        // Update existing product
        const productRef = ref(db, `Watch/admin/products/${editingId}`);
        await update(productRef, product);
        setSuccess('Product updated successfully!');
      } else {
        // Add new product
        const productsRef = ref(db, 'Watch/admin/products');
        await push(productsRef, product);
        setSuccess('Product added successfully!');
      }
      
      setTimeout(() => {
        setSuccess(false);
        resetForm();
        setView('table');
      }, 2000);
    } catch (error) {
      console.error("Error saving product: ", error);
      setSuccess('Error saving product. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setProduct({
      name: '',
      description: '',
      price: '',
      category: 'watches',
      type: '',
      brand: '',
      images: [],
      stock: '',
      featured: false,
      discount: 0,
      specifications: {
        material: '',
        colors: [],
        material: [],
        waterResistant: 'no',
        warranty: '',
        gender: 'unisex'
      }
    });
    setImageUrls([]);
    setEditingId(null);
    setActiveTab('basic');
    setCurrentColor('');
    setCurrentMaterial('');
  };

  const handleEdit = (product) => {
    setProduct({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      category: product.category || 'watches',
      type: product.type || '',
      brand: product.brand || '',
      images: product.images || [],
      stock: product.stock || '',
      featured: product.featured || false,
      discount: product.discount || 0,
      specifications: {
        material: product.specifications?.material || '',
        colors: product.specifications?.colors || [],
        material: product.specifications?.material || [],
        waterResistant: product.specifications?.waterResistant === true || product.specifications?.waterResistant === 'yes' ? 'yes' : 'no',
        warranty: product.specifications?.warranty || '',
        gender: product.specifications?.gender || 'unisex'
      }
    });
    setImageUrls(product.images || []);
    setEditingId(product.id);
    setView('form');
    window.scrollTo(0, 0);
  };

  const handleDelete = async (productId) => {
    try {
      const productRef = ref(db, `Watch/admin/products/${productId}`);
      await remove(productRef);
      setSuccess('Product deleted successfully!');
      setTimeout(() => setSuccess(false), 2000);
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting product: ", error);
      setSuccess('Error deleting product. Please try again.');
    }
  };

  const filteredProducts = products.filter(product => {
    const searchLower = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.brand.toLowerCase().includes(searchLower) ||
      product.category.toLowerCase().includes(searchLower) ||
      product.type.toLowerCase().includes(searchLower)
    );
  });

  // Inline styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '24px'
    },
    wrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '32px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    title: {
      fontSize: '30px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    subtitle: {
      color: '#4b5563',
      margin: '4px 0 0 0'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    primaryButton: {
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none'
    },
    successMessage: {
      marginBottom: '24px',
      padding: '16px',
      backgroundColor: '#d1fae5',
      borderLeft: '4px solid #10b981',
      color: '#065f46',
      borderRadius: '4px'
    },
    errorMessage: {
      marginBottom: '24px',
      padding: '16px',
      backgroundColor: '#fee2e2',
      borderLeft: '4px solid #ef4444',
      color: '#991b1b',
      borderRadius: '4px'
    },
    searchContainer: {
      marginBottom: '24px',
      display: 'flex',
      gap: '12px'
    },
    searchInput: {
      flex: 1,
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    tableHeader: {
      backgroundColor: '#f3f4f6',
      textAlign: 'left',
      padding: '12px 16px',
      fontWeight: '500',
      fontSize: '14px'
    },
    tableCell: {
      padding: '12px 16px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px'
    },
    actionCell: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      border: 'none',
      fontSize: '13px',
      fontWeight: '500'
    },
    editButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    confirmButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    cancelButton: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    imageThumbnail: {
      width: '50px',
      height: '50px',
      objectFit: 'cover',
      borderRadius: '4px'
    },
    formContainer: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    },
    tabs: {
      borderBottom: '1px solid #e5e7eb'
    },
    tabNav: {
      display: 'flex',
      overflowX: 'auto'
    },
    tabButton: {
      padding: '16px 24px',
      textAlign: 'center',
      borderBottom: '2px solid transparent',
      fontWeight: '500',
      fontSize: '14px',
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    },
    activeTab: {
      borderBottomColor: '#3b82f6',
      color: '#2563eb'
    },
    inactiveTab: {
      color: '#6b7280'
    },
    form: {
      padding: '32px'
    },
    inputGroup: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '4px'
    },
    input: {
      width: '100%',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s',
      fontSize: '14px'
    },
    textarea: {
      width: '100%',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      minHeight: '120px',
      outline: 'none',
      transition: 'all 0.2s',
      fontSize: '14px'
    },
    select: {
      width: '100%',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      backgroundColor: 'white',
      outline: 'none',
      transition: 'all 0.2s',
      fontSize: '14px'
    },
    checkbox: {
      height: '16px',
      width: '16px',
      color: '#3b82f6',
      borderColor: '#d1d5db',
      borderRadius: '4px',
      marginRight: '8px'
    },
    uploadArea: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px 24px',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      marginTop: '8px'
    },
    uploadContent: {
      textAlign: 'center'
    },
    uploadIcon: {
      height: '48px',
      width: '48px',
      color: '#9ca3af',
      margin: '0 auto'
    },
    uploadText: {
      display: 'flex',
      fontSize: '14px',
      color: '#6b7280',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '4px'
    },
    uploadButton: {
      position: 'relative',
      backgroundColor: 'white',
      borderRadius: '6px',
      fontWeight: '500',
      color: '#2563eb',
      cursor: 'pointer'
    },
    fileInput: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      borderWidth: '0'
    },
    previewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
      gap: '16px',
      marginTop: '16px'
    },
    previewImage: {
      height: '100px',
      width: '100%',
      objectFit: 'cover',
      borderRadius: '8px'
    },
    deleteImageButton: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      backgroundColor: '#ef4444',
      color: 'white',
      borderRadius: '50%',
      padding: '2px',
      width: '20px',
      height: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: 'none'
    },
    imageContainer: {
      position: 'relative'
    },
    formActions: {
      marginTop: '40px',
      paddingTop: '24px',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px'
    },
    submitButton: {
      padding: '8px 24px',
      backgroundColor: '#2563eb',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '14px'
    },
    submitButtonDisabled: {
      backgroundColor: '#93c5fd',
      cursor: 'not-allowed'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px'
    },
    fullGrid: {
      gridColumn: '1 / -1'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center'
    },
    checkboxLabel: {
      marginLeft: '8px',
      fontSize: '14px',
      color: '#374151'
    },
    featuredBadge: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      marginLeft: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>
              {view === 'form' 
                ? (editingId ? 'Edit Product' : 'Add New Product') 
                : 'Product Management'}
            </h1>
            <p style={styles.subtitle}>
              {view === 'form' 
                ? 'Fill in the product details' 
                : 'View and manage your product catalog'}
            </p>
          </div>
          <div style={styles.buttonGroup}>
            {view === 'form' ? (
              <>
                <button 
                  onClick={() => {
                    resetForm();
                    setView('table');
                  }}
                  style={styles.button}
                >
                  View Products
                </button>
                <button 
                  onClick={() => resetForm()}
                  style={styles.button}
                  disabled={uploading}
                >
                  {editingId ? 'Cancel Edit' : 'Clear Form'}
                </button>
              </>
            ) : (
              <button 
                onClick={() => {
                  resetForm();
                  setView('form');
                }}
                style={{ ...styles.button, ...styles.primaryButton }}
              >
                Add New Product
              </button>
            )}
          </div>
        </div>

        {/* Success/Error Message */}
        {success && (
          <div style={typeof success === 'string' && success.includes('Error') 
            ? styles.errorMessage 
            : styles.successMessage
          }>
            <p>{success}</p>
          </div>
        )}

        {view === 'form' ? (
          /* Form View */
          <div style={styles.formContainer}>
            {/* Form Tabs */}
            <div style={styles.tabs}>
              <nav style={styles.tabNav}>
                <button
                  onClick={() => setActiveTab('basic')}
                  style={{
                    ...styles.tabButton,
                    ...(activeTab === 'basic' ? styles.activeTab : styles.inactiveTab)
                  }}
                >
                  Basic Information
                </button>
                <button
                  onClick={() => setActiveTab('pricing')}
                  style={{
                    ...styles.tabButton,
                    ...(activeTab === 'pricing' ? styles.activeTab : styles.inactiveTab)
                  }}
                >
                  Pricing & Inventory
                </button>
                <button
                  onClick={() => setActiveTab('media')}
                  style={{
                    ...styles.tabButton,
                    ...(activeTab === 'media' ? styles.activeTab : styles.inactiveTab)
                  }}
                >
                  Media
                </button>
                <button
                  onClick={() => setActiveTab('specs')}
                  style={{
                    ...styles.tabButton,
                    ...(activeTab === 'specs' ? styles.activeTab : styles.inactiveTab)
                  }}
                >
                  Specifications
                </button>
              </nav>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              {/* Basic Information Tab */}
              {activeTab === 'basic' && (
                <div style={{ ...styles.grid, ...styles.fullGrid }}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Product Name *</label>
                    <input
                      style={styles.input}
                      name="name"
                      type="text"
                      placeholder="e.g., Premium Chronograph Watch"
                      value={product.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Brand *</label>
                    <input
                      style={styles.input}
                      name="brand"
                      type="text"
                      placeholder="Brand Name"
                      value={product.brand}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div style={{ ...styles.inputGroup, ...styles.fullGrid }}>
                    <label style={styles.label}>Description *</label>
                    <textarea
                      style={styles.textarea}
                      name="description"
                      placeholder="Detailed product description..."
                      value={product.description}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Category *</label>
                    <select
                      style={styles.select}
                      name="category"
                      value={product.category}
                      onChange={handleChange}
                      required
                    >
                      <option value="watches">Watches</option>
                      <option value="sunglasses">Sunglasses</option>
                      <option value="Clocks">Clocks</option>
                    </select>
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Type *</label>
                    <input
                      style={styles.input}
                      name="type"
                      type="text"
                      placeholder="e.g., Chronograph, Aviator"
                      value={product.type}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Pricing & Inventory Tab */}
              {activeTab === 'pricing' && (
                <div style={{ ...styles.grid, ...styles.fullGrid }}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Price (₹) *</label>
                    <input
                      style={styles.input}
                      name="price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={product.price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Discount (%)</label>
                    <input
                      style={styles.input}
                      name="discount"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      value={product.discount}
                      onChange={handleChange}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Stock Quantity *</label>
                    <input
                      style={styles.input}
                      name="stock"
                      type="number"
                      min="0"
                      placeholder="Available quantity"
                      value={product.stock}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div style={styles.checkboxContainer}>
                    <input
                      id="featured"
                      name="featured"
                      type="checkbox"
                      style={styles.checkbox}
                      checked={product.featured}
                      onChange={handleChange}
                    />
                    <label htmlFor="featured" style={styles.checkboxLabel}>
                      Mark as featured product
                    </label>
                  </div>
                </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div style={{ ...styles.grid, ...styles.fullGrid }}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Product Images *</label>
                    <div style={styles.uploadArea}>
                      <div style={styles.uploadContent}>
                        <svg
                          style={styles.uploadIcon}
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div style={styles.uploadText}>
                          <label
                            htmlFor="images"
                            style={styles.uploadButton}
                          >
                            <span>Upload files</span>
                            <input
                              id="images"
                              name="images"
                              type="file"
                              style={styles.fileInput}
                              multiple
                              accept="image/*"
                              onChange={handleImageUpload}
                              required
                            />
                          </label>
                          <p>or drag and drop</p>
                        </div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </div>
                    {uploading && (
                      <p style={{ marginTop: '8px', fontSize: '14px', color: '#2563eb' }}>
                        Uploading images, please wait...
                      </p>
                    )}
                  </div>

                  {imageUrls.length > 0 && (
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Preview</label>
                      <div style={styles.previewGrid}>
                        {imageUrls.map((url, index) => (
                          <div key={index} style={styles.imageContainer}>
                            <img
                              src={url}
                              alt={`Preview ${index}`}
                              style={styles.previewImage}
                            />
                            <button
                              type="button"
                              style={styles.deleteImageButton}
                              onClick={() => {
                                const updatedUrls = [...imageUrls];
                                updatedUrls.splice(index, 1);
                                setImageUrls(updatedUrls);
                                setProduct(prev => ({
                                  ...prev,
                                  images: updatedUrls
                                }));
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}



              {/* Specifications Tab */}
{activeTab === 'specs' && (
  <div style={{ ...styles.grid, ...styles.fullGrid }}>
    <div style={styles.inputGroup}>
      <label style={styles.label}>Colors</label>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          style={{ ...styles.input, flex: 1 }}
          type="text"
          placeholder="Add a color (e.g., Black, Silver)"
          value={currentColor}
          onChange={(e) => setCurrentColor(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddColor()}
        />
        <button
          type="button"
          onClick={handleAddColor}
          style={{ ...styles.button, padding: '8px 16px' }}
          disabled={!currentColor.trim()}
        >
          Add
        </button>
      </div>
      
      {product.specifications.colors.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {product.specifications.colors.map((color, index) => (
            <div 
              key={index} 
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: '#f3f4f6',
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '14px'
              }}
            >
              {color}
              <button
                type="button"
                onClick={() => handleRemoveColor(color)}
                style={{ 
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: '16px',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    <div style={styles.inputGroup}>
      <label style={styles.label}>material</label>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          style={{ ...styles.input, flex: 1 }}
          type="text"
          placeholder="Add a material (e.g., Stainless Steel, Leather)"
          value={currentMaterial}
          onChange={(e) => setCurrentMaterial(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddMaterial()}
        />
        <button
          type="button"
          onClick={handleAddMaterial}
          style={{ ...styles.button, padding: '8px 16px' }}
          disabled={!currentMaterial.trim()}
        >
          Add
        </button>
      </div>
      
      {product.specifications.material.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {product.specifications.material.map((material, index) => (
            <div 
              key={index} 
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: '#f3f4f6',
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '14px'
              }}
            >
              {material}
              <button
                type="button"
                onClick={() => handleRemoveMaterial(material)}
                style={{ 
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: '16px',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Only show gender field if category is not 'Clocks' */}
    {product.category !== 'Clocks' && (
      <div style={styles.inputGroup}>
        <label style={styles.label}>Gender</label>
        <select
          style={styles.select}
          name="specifications.gender"
          value={product.specifications.gender}
          onChange={handleChange}
        >
          <option value="men">Men</option>
          <option value="women">Women</option>
          <option value="unisex">Unisex</option>
        </select>
      </div>
    )}
    
    <div style={styles.inputGroup}>
      <label style={styles.label}>Warranty</label>
      <input
        style={styles.input}
        name="specifications.warranty"
        type="text"
        placeholder="e.g., 2 Years"
        value={product.specifications.warranty}
        onChange={handleChange}
      />
    </div>

    {product.category === 'watches' && (
      <div style={styles.checkboxContainer}>
        <input
          id="specifications.waterResistant"
          name="specifications.waterResistant"
          type="checkbox"
          style={styles.checkbox}
          checked={product.specifications.waterResistant === 'yes'}
          onChange={handleChange}
        />
        <label htmlFor="specifications.waterResistant" style={styles.checkboxLabel}>
          Water Resistant
        </label>
      </div>
    )}
  </div>
)}

              {/* Form Actions */}
              <div style={styles.formActions}>
                <div>
                  {activeTab !== 'basic' && (
                    <button
                      type="button"
                      onClick={() => setActiveTab(prev => {
                        if (prev === 'pricing') return 'basic';
                        if (prev === 'media') return 'pricing';
                        if (prev === 'specs') return 'media';
                        return prev;
                      })}
                      style={styles.button}
                      disabled={uploading}
                    >
                      Previous
                    </button>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  {activeTab !== 'specs' && (
                    <button
                      type="button"
                      onClick={() => setActiveTab(prev => {
                        if (prev === 'basic') return 'pricing';
                        if (prev === 'pricing') return 'media';
                        if (prev === 'media') return 'specs';
                        return prev;
                      })}
                      style={styles.button}
                      disabled={uploading}
                    >
                      Next
                    </button>
                  )}
                  
                  {activeTab === 'specs' && (
                    <button
                      type="submit"
                      disabled={uploading}
                      style={{
                        ...styles.submitButton,
                        ...(uploading && styles.submitButtonDisabled)
                      }}
                    >
                      {uploading 
                        ? (editingId ? 'Updating...' : 'Adding...') 
                        : (editingId ? 'Update Product' : 'Add Product')}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        ) : (
          /* Table View */
          <div>
            <div style={styles.searchContainer}>
              <input
                type="text"
                placeholder="Search products..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Image</th>
                  <th style={styles.tableHeader}>Name</th>
                  <th style={styles.tableHeader}>Brand</th>
                  <th style={styles.tableHeader}>Category</th>
                  <th style={styles.tableHeader}>Price</th>
                  <th style={styles.tableHeader}>Stock</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <tr key={product.id}>
                      <td style={styles.tableCell}>
                        {product.images?.length > 0 ? (
                          <img 
                            src={product.images[0]} 
                            alt={product.name} 
                            style={styles.imageThumbnail}
                          />
                        ) : (
                          'No image'
                        )}
                      </td>
                      <td style={styles.tableCell}>
                        {product.name}
                        {product.featured && (
                          <span style={styles.featuredBadge}>Featured</span>
                        )}
                      </td>
                      <td style={styles.tableCell}>{product.brand}</td>
                      <td style={styles.tableCell}>
                        {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                      </td>
                      <td style={styles.tableCell}>
                        ₹{parseFloat(product.price).toFixed(2)}
                        {product.discount > 0 && (
                          <div style={{ color: '#ef4444', fontSize: '12px' }}>
                            {product.discount}% off
                          </div>
                        )}
                      </td>
                      <td style={styles.tableCell}>{product.stock}</td>
                      <td style={styles.tableCell}>
                        <div style={styles.actionCell}>
                          <button
                            onClick={() => handleEdit(product)}
                            style={{ ...styles.actionButton, ...styles.editButton }}
                          >
                            Edit
                          </button>
                          {deleteConfirm === product.id ? (
                            <>
                              <button
                                onClick={() => handleDelete(product.id)}
                                style={{ ...styles.actionButton, ...styles.confirmButton }}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                style={{ ...styles.actionButton, ...styles.cancelButton }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(product.id)}
                              style={{ ...styles.actionButton, ...styles.deleteButton }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ ...styles.tableCell, textAlign: 'center' }}>
                      {products.length === 0 ? 'No products found' : 'No matching products'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddProduct;


